import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../Redux/Hooks'
import {
  getAccountInfoAPI,
  getAllPackageAPI,
  getCashfreeVendorStatusAPI,
} from '../../../Redux/ApiCalls/Dashboard/PackageAPI'
import { AccountDetailsType, Package } from '../../../Data/package.dto'
import PackageItem from '../../atoms/Dashboard/Package/PackageItem'
import UnderLine from '../../atoms/Login/UnderLine'
import {
  clearCurrentPackage,
  clearError,
} from '../../../Redux/Slice/Dashboard/PackageSlice'
import AddAccountInformationModal from '../../atoms/Dashboard/Package/AddAccountInformationModal'
import { showErrorToast } from '../../atoms/Utlis/Toast'
import { useDevmode } from '../../../hooks/useDevmode'
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from './Marketplace/MarketplaceEmptyState'
import {
  Package as PackageIcon,
  Plus,
  Sparkles,
  ArrowLeft,
  Pencil,
  Building2,
  BadgeCheck,
} from 'lucide-react'
import { device } from '../../atoms/Utlis/media'
import PermissionGate from '../../atoms/Utlis/PermissionGate'
import { usePermissions } from '../../../hooks/usePermissions'

const AllPackagesPage: React.FC = () => {
  const navigate = useNavigate()
  const {
    packages,
    isPackageUpdate,
    loading,
    account,
    isAccountUpdated,
    cashfreeVendorStatus,
  } = useAppSelector((state) => state.package)
  const { user } = useAppSelector((state) => state.auth)
  const [isLoading, setIsLoading] = useState(true)
  const [currentPackages, setCurrentPackages] = useState<Package[]>([])
  const [userAccount, setUserAccount] = useState<AccountDetailsType>()
  const [editAccountPopup, setEditAccountPopup] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const isDevMode = useDevmode()
  const { hasPermission } = usePermissions()

  useEffect(() => {
    setIsLoading(true)
    if (isPackageUpdate && user) {
      dispatch(getAllPackageAPI({ user_id: user.id })).finally(() => {
        setIsLoading(false)
      })
    } else if (packages && packages.length) {
      setCurrentPackages(packages)
      setIsLoading(false)
    }

    if (!isPackageUpdate) {
      setIsLoading(false)
    }
    return () => {
      dispatch(clearError())
    }
  }, [isPackageUpdate, packages, user, dispatch])

  useEffect(() => {
    if (isAccountUpdated) {
      dispatch(getAccountInfoAPI())
      dispatch(getCashfreeVendorStatusAPI())
    }
  }, [dispatch, isAccountUpdated])

  useEffect(() => {
    if (account) {
      setUserAccount(account)
    }
  }, [account])

  const handleAddPackage = () => {
    if ((userAccount && userAccount.account_number) || isDevMode) {
      dispatch(clearCurrentPackage())
      navigate('/dashboard/package/single')
    } else {
      showErrorToast('Please add account details first.')
    }
  }

  const getStatusStyle = (status: string) => {
    const s = status.toLowerCase()
    if (s.includes('active') || s.includes('success')) return { bg: 'rgba(49,166,106,0.12)', color: '#31a66a' }
    if (s.includes('pending')) return { bg: 'rgba(240,154,62,0.15)', color: '#f09a3e' }
    if (s.includes('reject') || s.includes('fail')) return { bg: 'rgba(228,72,98,0.12)', color: '#e44862' }
    return { bg: 'rgba(23,23,23,0.08)', color: 'rgba(23,23,23,0.65)' }
  }

  const vendorStatus = cashfreeVendorStatus?.cashfree_vendor_status
  const statusStyle = vendorStatus ? getStatusStyle(String(vendorStatus)) : null

  return (
    <PageContainer>
      {editAccountPopup && (
        <AddAccountInformationModal
          account_number={userAccount?.account_number}
          ifsc_code={userAccount?.ifsc_code}
          beneficiary_name={userAccount?.beneficiary_name}
          pan={userAccount?.pan}
          uidai={userAccount?.uidai}
          tnc_accepted={userAccount?.tnc_accepted}
          address_line_1={userAccount?.address_line_1}
          address_line_2={userAccount?.address_line_2}
          city={userAccount?.city}
          state={userAccount?.state}
          zip_code={userAccount?.zip_code}
          country={userAccount?.country}
          user_address={userAccount?.user_address}
          onClose={() => setEditAccountPopup(false)}
        />
      )}

      {/* <BackRow>
        <BackButton type="button" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
          Back
        </BackButton>
      </BackRow> */}

      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Studio offerings
              </HeroBadge>
              <PageTitle className="linear-text">Packages</PageTitle>
              <PageSubtitle>
                Create and manage photography packages for customers to browse and book.
              </PageSubtitle>
            </div>
          </HeroText>
          <PermissionGate permission="packages.create">
            <AddButton type="button" onClick={handleAddPackage}>
              <Plus size={18} strokeWidth={2.4} />
              Add package
            </AddButton>
          </PermissionGate>
        </HeroContent>
      </Hero>

      <BankCard>
        <BankCardHeader>
          <BankCardTitleRow>
            <BankIconWrap>
              <Building2 size={18} />
            </BankIconWrap>
            <div>
              <BankCardTitle>Payout account</BankCardTitle>
              <BankCardSubtitle>Bank details for receiving package payments</BankCardSubtitle>
            </div>
          </BankCardTitleRow>
          <EditBtn type="button" onClick={() => setEditAccountPopup(true)} title="Edit account">
            <Pencil size={16} />
          </EditBtn>
        </BankCardHeader>

        <BankGrid>
          <BankField>
            <BankLabel>Account name</BankLabel>
            <BankValue>{userAccount?.beneficiary_name || '—'}</BankValue>
          </BankField>
          <BankField>
            <BankLabel>Account number</BankLabel>
            <BankValue>{userAccount?.account_number || '—'}</BankValue>
          </BankField>
          <BankField>
            <BankLabel>IFSC code</BankLabel>
            <BankValue>{userAccount?.ifsc_code || '—'}</BankValue>
          </BankField>
          <BankField>
            <BankLabel>Vendor status</BankLabel>
            {vendorStatus && statusStyle ? (
              <StatusBadge $bg={statusStyle.bg} $color={statusStyle.color}>
                <BadgeCheck size={12} />
                {String(vendorStatus).toUpperCase()}
              </StatusBadge>
            ) : (
              <BankValue>—</BankValue>
            )}
          </BankField>
        </BankGrid>

        {cashfreeVendorStatus?.remarks && (
          <RemarksRow>
            <BankLabel>Remarks</BankLabel>
            <RemarksText>{cashfreeVendorStatus.remarks}</RemarksText>
          </RemarksRow>
        )}
      </BankCard>

      {!isLoading && (
        <StatsGrid>
          <WalletHero>
            <WalletIconWrap>
              <PackageIcon size={16} />
            </WalletIconWrap>
            <WalletHeroBody>
              <WalletHeroValue>{currentPackages.length}</WalletHeroValue>
              <WalletHeroLabel>Total packages</WalletHeroLabel>
            </WalletHeroBody>
          </WalletHero>
        </StatsGrid>
      )}

      <Panel>
        {isLoading || loading ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : currentPackages.length === 0 ? (
          <MarketplaceEmptyState
            padding="48px 22px"
            icon={<PackageIcon size={36} strokeWidth={1.5} />}
            title="No packages yet"
            description="Create your first package so customers can browse and book your photography offerings."
            actions={
              hasPermission('packages.create')
                ? [
                    {
                      label: 'Create Your First Package',
                      onClick: handleAddPackage,
                      icon: <Plus size={15} />,
                    },
                  ]
                : []
            }
            hint={{
              icon: <Sparkles size={13} />,
              text: 'Add account details first, then publish packages for booking',
            }}
          />
        ) : (
          <PackageList>
            {currentPackages.map((packageData) => (
              <PackageRow key={packageData.id}>
                <PackageItem packageData={packageData} />
                {/* <UnderLine width={100} isPercent={true} /> */}
              </PackageRow>
            ))}
          </PackageList>
        )}
      </Panel>
    </PageContainer>
  )
}

export default AllPackagesPage

const gradient = 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  @media ${device.tablet} { padding: 16px; }
`

const BackRow = styled.div`
  flex-shrink: 0;
`

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 10px;
  background: #fff;
  color: rgba(23, 23, 23, 0.72);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

const HeroText = styled.div`
  min-width: 0;
`

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  width: fit-content;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  white-space: nowrap;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(201, 77, 170, 0.28);
  }
`

const BankCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 22px 24px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
`

const BankCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`

const BankCardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const BankIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201, 77, 170, 0.1);
  color: #c94daa;
  flex-shrink: 0;
`

const BankCardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`

const BankCardSubtitle = styled.p`
  margin: 2px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
`

const EditBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background: ${gradient};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease;
  flex-shrink: 0;
  &:hover { transform: translateY(-1px); }
`

const BankGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`

const BankField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const BankLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const BankValue = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
`

const StatusBadge = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  padding: 4px 10px;
  border-radius: 999px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`

const RemarksRow = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const RemarksText = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 280px);
  gap: 12px;
  flex-shrink: 0;
`

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`

const Panel = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  overflow: hidden;
  flex-shrink: 0;
`

const PackageList = styled.div`
  padding: 22px;
  overflow-y: auto;
`

const PackageRow = styled.div`
  margin-bottom: 8px;
`
